// Globals
import { useTranslation } from 'react-i18next';

// Components
import { IFormInstance } from '@/components';
import { Button, Input, PhoneInput } from '@/ui';
import StatusTag from '../StatusTag/StatusTag';
import { FormColumn, FormRow, StyledFooter, StyledFormItem, StyledWrapper } from '../Styled';
import UserStatusChange from '../UserStatusChange/UserStatusChange';
import {
  HorizontalDivider,
  StyledForm,
  StyledLabel,
  StyledSubTitle,
  StyledValue,
  StyledValueBlock,
} from './CreateUserFormStyles';

// Modules
import { PlaceAutocomplete } from '@/modules/Maps';

// Models
import { IUser, UserStatus } from '@/models/users.model';

// Helpers
import { formatDate } from '@/utils';
import { TUserFormValues, UserFormFieldsNames } from '../../helpers/types';
import { useUserFormRules } from '../../hooks';

interface ICreateUserForm {
  userDetails?: IUser | null;
  handleSubmit: (values: TUserFormValues) => void;
  updateUserStatus: (id: string, status: UserStatus) => void;
  isLoading: boolean;
  form: IFormInstance;
  initialValues?: IUser | null;
}

const CreateUserForm = ({
  handleSubmit,
  isLoading,
  initialValues,
  form,
  userDetails,
  updateUserStatus,
}: ICreateUserForm) => {
  const { t } = useTranslation(['users', 'common']);
  const rules = useUserFormRules();

  const placeholder = t('common_placeholder_input', { ns: 'common' });

  return (
    <StyledWrapper>
      <StyledForm onFinish={handleSubmit} initialValues={initialValues ?? {}} form={form} name={'create-form'}>
        <FormColumn>
          <FormColumn>
            <StyledSubTitle>{t('user_creation_details')}</StyledSubTitle>
            <StyledFormItem
              name={UserFormFieldsNames.NAME}
              label={t('users_list_name')}
              rules={rules[UserFormFieldsNames.NAME]}
            >
              <Input placeholder={placeholder} />
            </StyledFormItem>

            <FormRow>
              <StyledFormItem
                name={UserFormFieldsNames.PHONE_NUMBER}
                rules={rules[UserFormFieldsNames.PHONE_NUMBER]}
                label={t('users_list_phone_number')}
              >
                <PhoneInput />
              </StyledFormItem>
              <StyledFormItem
                name={UserFormFieldsNames.EMAIL}
                label={t('users_list_email')}
                rules={rules[UserFormFieldsNames.EMAIL]}
              >
                <Input placeholder={placeholder} />
              </StyledFormItem>
            </FormRow>

            <FormRow>
              <StyledFormItem name={UserFormFieldsNames.ADDRESS} label={t('users_list_address')}>
                <PlaceAutocomplete />
              </StyledFormItem>
              <StyledFormItem
                name={UserFormFieldsNames.INVOICE_ADDRESS}
                label={t('users_list_invoice_address')}
                rules={rules[UserFormFieldsNames.INVOICE_ADDRESS]}
              >
                <PlaceAutocomplete />
              </StyledFormItem>
            </FormRow>
          </FormColumn>
          {userDetails && (
            <FormColumn>
              <StyledSubTitle>{t('user_creation_properties')}</StyledSubTitle>
              <FormRow>
                <StyledValueBlock>
                  <StyledLabel>{t('users_list_registration_date')}</StyledLabel>
                  <StyledValue>{formatDate(userDetails?.created ?? '')}</StyledValue>
                </StyledValueBlock>
                {userDetails?.last_login_at && (
                  <StyledValueBlock>
                    <StyledLabel>{t('users_list_login_date')}</StyledLabel>
                    <StyledValue>{formatDate(userDetails?.last_login_at ?? '')}</StyledValue>
                  </StyledValueBlock>
                )}
              </FormRow>
              <HorizontalDivider />
              {userDetails?.tripletex_customer_number && (
                <>
                  <StyledValueBlock>
                    <StyledLabel>{t('users_list_id')}</StyledLabel>
                    <StyledValue>{userDetails?.tripletex_customer_number}</StyledValue>
                  </StyledValueBlock>
                  <HorizontalDivider />
                </>
              )}
              <FormColumn>
                <StyledValueBlock>
                  <StyledLabel>{t('users_list_status')}</StyledLabel>
                  <StatusTag status={userDetails?.status} />
                </StyledValueBlock>
                <StyledValueBlock>
                  <UserStatusChange user={userDetails} updateUserStatus={updateUserStatus} />
                </StyledValueBlock>
              </FormColumn>
            </FormColumn>
          )}
        </FormColumn>
      </StyledForm>
      <StyledFooter>
        <Button shape="round" htmlType="submit" form={'create-form'} type={'primary'} loading={isLoading}>
          {t(userDetails ? 'user_edit_save' : 'user_create_save')}
        </Button>
      </StyledFooter>
    </StyledWrapper>
  );
};

export default CreateUserForm;
